import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import en from './translations/en';
import ms from './translations/ms';
import id from './translations/id';

export const DEFAULT_LANGUAGE = 'ms';
// Cross-language fallback target. ENGLISH is the canonical fallback so that
// English mode can NEVER silently display Bahasa Melayu when a key is missing:
// selected dict -> en dict -> defaultValue -> raw key. Bahasa Melayu / Bahasa
// Indonesia modes gracefully fall back to English when their own key is absent.
export const FALLBACK_LANGUAGE = 'en';
export const LANGUAGE_STORAGE_KEY = 'cheeco_language';

export const languages = [
  { code: 'ms', label: 'Bahasa Melayu' },
  { code: 'en', label: 'English' },
  { code: 'id', label: 'Bahasa Indonesia' },
];

const dictionaries = { en, ms, id };
const I18nContext = createContext(null);

function getNestedValue(source, key) {
  return key.split('.').reduce((current, part) => (current && Object.prototype.hasOwnProperty.call(current, part) ? current[part] : undefined), source);
}

function interpolate(value, params = {}) {
  if (typeof value !== 'string') return value;
  return value.replace(/{{\s*([\w.]+)\s*}}/g, (_, token) => String(params[token] ?? ''));
}

function normalizeLanguage(code) {
  return dictionaries[code] ? code : DEFAULT_LANGUAGE;
}

function getInitialLanguage() {
  // 1. Respect the user's explicit browser choice first.
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (stored) {
    return normalizeLanguage(stored);
  }

  // 2. Fall back to the authenticated user's stored language.
  try {
    const authUser = localStorage.getItem('auth.user');
    if (authUser) {
      const parsed = JSON.parse(authUser);
      if (parsed && parsed.language) {
        return normalizeLanguage(parsed.language);
      }
    }
  } catch {
    // ignore parse errors
  }

  return normalizeLanguage(DEFAULT_LANGUAGE);
}

export function I18nProvider({ children }) {
  const [language, setLanguageState] = useState(() => getInitialLanguage());

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value = useMemo(() => {
    const dictionary = dictionaries[language] || dictionaries[DEFAULT_LANGUAGE];

    return {
      language,
      languages,
      setLanguage(nextLanguage) {
        const normalized = normalizeLanguage(nextLanguage);
        localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
        document.documentElement.lang = normalized;
        setLanguageState(normalized);
      },
      t(key, params = {}) {
        const localized = getNestedValue(dictionary, key);
        const fallback = getNestedValue(dictionaries[FALLBACK_LANGUAGE], key);
        let value = localized ?? fallback;
        if (value === undefined && Object.prototype.hasOwnProperty.call(params, 'defaultValue')) {
          value = params.defaultValue;
        }
        return interpolate(value ?? key, params);
      },
    };
  }, [language]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
}

export function translateStatic(key, params = {}) {
  const fallback = getNestedValue(dictionaries[FALLBACK_LANGUAGE], key);
  return interpolate(fallback ?? key, params);
}
